import { BiCardQuery } from '@euler/model/bi/blend';
import { BiDashboardFilters } from '@euler/model/bi/dashboard';
import { BiQueryResult } from '@euler/model/bi/result';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useState } from 'react';
import { biErrorMessage } from '../../../functions/biErrors';

export type BiPreviewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; result: BiQueryResult }
  | { kind: 'failed'; message: string };

const kDebounceMs = 400;

/** A result already read for one spec under one set of filters. */
export interface BiPreviewKnown {
  /** `biPreviewKey` of the spec and filters it was read for. */
  key: string;
  result: BiQueryResult;
}

/** What tells one preview's query from another. */
export function biPreviewKey(
  spec: BiCardQuery,
  filters: BiDashboardFilters,
): string {
  return JSON.stringify({ spec, filters });
}

/**
 * The editor's live preview: the spec as typed, run a moment after under
 * the filters the card is viewed with, as the dashboard will run it. The
 * result the card already showed stands in while spec and filters are the
 * ones it was read for: an untouched card costs no query.
 */
export function useBiPreview(
  spec: BiCardQuery | null,
  filters: BiDashboardFilters,
  known: BiPreviewKnown | null,
): BiPreviewState {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiPreviewState>({ kind: 'idle' });
  const key = spec ? biPreviewKey(spec, filters) : null;
  const reused = !!known && key === known.key ? known.result : null;
  useEffect(() => {
    if (!key) {
      setState({ kind: 'idle' });
      return;
    }
    if (reused) {
      setState({ kind: 'ready', result: reused });
      return;
    }
    let active = true;
    setState({ kind: 'loading' });
    const timer = setTimeout(() => {
      const query = JSON.parse(key) as {
        spec: BiCardQuery;
        filters: BiDashboardFilters;
      };
      biService
        .query(query.spec, query.filters)
        .then(result => {
          if (active) setState({ kind: 'ready', result });
        })
        .catch(error => {
          console.error('BI preview failed', error);
          if (active)
            setState({
              kind: 'failed',
              message: biErrorMessage(error, '预览没有加载出来。'),
            });
        });
    }, kDebounceMs);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [biService, key, reused]);
  return state;
}
